import { useEffect, useState, useRef } from "react";
import EmojiPicker, { EmojiClickData, EmojiStyle, Theme } from "emoji-picker-react";
import shared from "../data/js/shared";
import Img from "./img";
import { useTheme } from "../contexts/ThemeProvider";
import { useContactCaptureAsk } from "./contact-capture/useContactCaptureAsk";
const { buyerInputPlaceholder, sellerInputPlaceholder } =
	shared.chat;

const ChatInput = ({
	chatMessage,
	sendChatMessage,
	setChatMessage,
	listingId,
	chatId,
	seller,
}) => {
	// Use state for placeholder to avoid hydration mismatch
	const [placeholder, setPlaceholder] = useState(buyerInputPlaceholder);
	const [showEmojiPicker, setShowEmojiPicker] = useState(false);
	const emojiContainerRef = useRef<HTMLDivElement>(null);
	const { theme } = useTheme();
	// A phone-only account with no name or email is asked for them, once a visit,
	// before its message goes (components/contact-capture). Never blocks the send.
	const askFirst = useContactCaptureAsk();

	useEffect(() => {
		// Update placeholder on client side after hydration
		// eslint-disable-next-line react-hooks/set-state-in-effect -- intentional two-pass render: state starts as buyerInputPlaceholder for SSR, then updates post-hydration to avoid a hydration mismatch
		setPlaceholder(seller ? sellerInputPlaceholder : buyerInputPlaceholder);
	}, [seller]);

	// The box empties at once, so sending feels instant. A message the server
	// refuses (moderation, a block, a network error) comes back as null, and the
	// text goes back in the box: it used to vanish, and the sender had to type
	// it again from memory to fix one word. Anything typed since is kept.
	const submit = async (text: string) => {
		setChatMessage("");
		const result = await sendChatMessage(text, listingId, chatId);
		if (result === null) {
			setChatMessage((current: string) => current || text);
		}
	};

	// Handle emoji selection
	const onEmojiClick = (emojiData: EmojiClickData) => {
		setChatMessage((prev: string) => prev + emojiData.emoji);
		setShowEmojiPicker(false);
	};

	// Click outside to close
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (showEmojiPicker && emojiContainerRef.current && !emojiContainerRef.current.contains(e.target as Node)) {
				setShowEmojiPicker(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showEmojiPicker]);

	return (
		<div className="chat-input-cont same-color-2">
			<form
				className="chat-input-form"
				onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
					e.preventDefault();
					if (chatMessage) askFirst("chat", () => void submit(chatMessage));
				}}
			>
				<div className="emoji-picker-container" ref={emojiContainerRef}>
					<button
						type="button"
						className="emoji-picker-button"
						onClick={() => setShowEmojiPicker(!showEmojiPicker)}
					>
						<span className="emoji-icon">😊</span>
					</button>
					{showEmojiPicker && (
						<div className="emoji-picker-popup">
							<EmojiPicker onEmojiClick={onEmojiClick} emojiStyle={EmojiStyle.APPLE} theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT} />
						</div>
					)}
				</div>
				<input
					type="text"
					className="chat-message"
					data-testid="chat-input"
					placeholder={placeholder}
					name="chat-message"
					value={chatMessage}
					onInput={(e) => {
						setChatMessage((e.target as HTMLInputElement).value);
					}}
				/>
				<button className="send-chat-button" data-testid="chat-send-button">
					<Img src="/images/svg/send-chat-arrow.svg" />
				</button>
			</form>
		</div>
	);
};
export default ChatInput;
